import { MutationCache, QueryCache, QueryClient, type Query } from "@tanstack/react-query";
import { FRESHNESS, freshnessOf } from "./policies";
import { ScopeChangedError } from "./scope";

/**
 * The app's one QueryClient (NG-1313).
 *
 * A module singleton rather than something created in a component, so a React
 * Router `clientLoader` can read and prime the cache before the route renders,
 * the same way loaders reach the MobX root store through `getRootStore()`.
 * `root.tsx` hands the same instance to `QueryClientProvider`. Tests get a fresh
 * client each (`render-with-providers.tsx`, `setQueryClientForTesting`).
 *
 * The lifecycle it owns, and nothing else:
 * - **Defaults are the conservative row** of the freshness matrix
 *   (access-sensitive), so a query that forgets its class is never served from
 *   cache. Every query definition spreads its own row from `policies.ts`.
 * - **Query's retry is off**, for queries and mutations. A read's retries are
 *   owned by `runRead` in `policies.ts`; Query's default three would otherwise
 *   stack on top of them.
 * - **Every failure is logged here**, once, at the layer that knows the key.
 * - **Access-sensitive entries are evicted** once they settle with no
 *   observer, so a later mount can never be shown an old answer.
 */
export function createQueryClient(): QueryClient {
  const conservative = FRESHNESS["access-sensitive"];
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => logFailure("query", query.queryKey, error),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        logFailure("mutation", mutation.options.mutationKey, error),
    }),
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: conservative.staleTime,
        gcTime: conservative.gcTime,
        refetchOnMount: conservative.refetchOnMount,
        refetchOnWindowFocus: conservative.refetchOnWindowFocus,
        refetchOnReconnect: conservative.refetchOnReconnect,
      },
      mutations: { retry: false },
    },
  });
  evictSettledAccessSensitive(client.getQueryCache());
  return client;
}

/**
 * Logs the key's domain and shape, not its values: a representation can carry
 * user-typed search text, and the tenant/principal prefix adds nothing to act
 * on. A read abandoned by a sign-out is expected, so it only warns.
 */
function logFailure(kind: "query" | "mutation", key: readonly unknown[] | undefined, error: Error) {
  const domain = key?.[2] ?? "(unkeyed)";
  if (error instanceof ScopeChangedError) {
    console.warn(`[queries] ${kind} ${String(domain)} settled after the session changed`, error);
    return;
  }
  console.error(`[queries] ${kind} ${String(domain)} failed`, { keyLength: key?.length }, error);
}

/**
 * Contract §2: an access-sensitive answer may be shared by consumers that
 * overlap its request, and by nobody after. `gcTime` alone would keep it for
 * five minutes, so remove it the moment it is both settled and unobserved.
 * Runs on observer removal (the last consumer unmounted after the settle) and
 * on every state update (a request settled after its last consumer left).
 */
function evictSettledAccessSensitive(cache: QueryCache): void {
  cache.subscribe((event) => {
    if (event.type !== "observerRemoved" && event.type !== "updated") return;
    const query: Query = event.query;
    if (freshnessOf(query.meta) !== "access-sensitive") return;
    if (query.getObserversCount() > 0 || query.state.fetchStatus !== "idle") return;
    if (cache.get(query.queryHash) !== query) return;
    cache.remove(query);
  });
}

let instance: QueryClient | null = null;

/** The app's QueryClient; created on first use, from a loader or from `root.tsx`. */
export function getQueryClient(): QueryClient {
  instance ??= createQueryClient();
  return instance;
}

/** The client if one exists, without creating one: a sign-out must not. */
export function peekQueryClient(): QueryClient | null {
  return instance;
}

/** Point the singleton at a test's own client; `null` restores lazy creation. */
export function setQueryClientForTesting(client: QueryClient | null): void {
  instance = client;
}
